import { classifyUrl, tokenizeChatText } from '@core/types';
import { useAppSelector } from '@online-webinar/store';
import { Fragment, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkWarningModal } from '../../molecules/LinkWarningModal';
import { MessageTextProps } from './types';

const linkClassName = 'underline underline-offset-3 decoration-primary hover:text-primary transition-colors duration-200 ease-in-out';

// Plain text nodes + explicit link tokens: React escapes everything else, so sender-authored HTML can
// never become an element (the previous dangerouslySetInnerHTML + DOMPurify kept <a href> alive).
// Unknown links are buttons, not anchors with preventDefault: middle-click, modifier keys and the
// context menu cannot bypass a button.
export const MessageText = ({ messageBody }: MessageTextProps) => {
    const { t } = useTranslation();
    const policy = useAppSelector((state) => state.chatLinkPolicy);
    const tokens = useMemo(() => tokenizeChatText(messageBody), [messageBody]);
    const [pendingUrl, setPendingUrl] = useState<URL | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    const closeWarning = () => {
        setPendingUrl(null);
        triggerRef.current?.focus();
    };

    return (
        <>
            <span className="whitespace-pre-wrap">
                {tokens.map((token, index) => {
                    if (token.type === 'text') {
                        return <Fragment key={index}>{token.value}</Fragment>;
                    }
                    const kind = classifyUrl(token.url, policy);
                    if (kind === 'blocked') {
                        return <Fragment key={index}>{token.raw}</Fragment>;
                    }
                    if (kind === 'self' || kind === 'allowed') {
                        return (
                            <a key={index} href={token.url.href} target="_blank" rel="noopener noreferrer" className={linkClassName}>
                                {token.raw}
                                <span className="sr-only">{t('Generic.Link.OpensInNewTab')}</span>
                            </a>
                        );
                    }
                    return (
                        <button
                            key={index}
                            type="button"
                            data-testid="chat-link-unverified"
                            aria-haspopup="dialog"
                            className={`${linkClassName} cursor-pointer max-w-full text-left wrap-break-word`}
                            onClick={(event) => {
                                triggerRef.current = event.currentTarget;
                                setPendingUrl(token.url);
                            }}
                        >
                            {token.raw}
                        </button>
                    );
                })}
            </span>
            {pendingUrl && <LinkWarningModal url={pendingUrl} onClose={closeWarning} />}
        </>
    );
};
